import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { database } from "./firebase";
import { ref, get } from "firebase/database";
import sfRoundedBold from "../assets/fonts/SF-Pro-Rounded-Bold.otf";
import sfRoundedMedium from "../assets/fonts/SF-Pro-Rounded-Medium.otf";
import sfRoundedLight from "../assets/fonts/SF-Pro-Rounded-Light.otf";
import moodIcon from "../assets/moodstream-icon.png";

const ACCENT = "#f700ff";
const BG_TOP = "#05050b";
const BG_BOTTOM = "#1c0930";
const PAGE_BG = `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOTTOM} 100%)`;

const DEFAULT_THUMB =
  "https://firebasestorage.googleapis.com/v0/b/pokefinder-9c71b.appspot.com/o/logo_img%2Fmoodsmiley.png?alt=media";

const SHARE_DESCRIPTION =
  "Join this gallery and post together on MoodStream.";

/**
 * URLSearchParams.get("thumbnail") is truncated if the share link left raw `&`
 * inside the Firebase URL (everything after the first `&` becomes separate params).
 * Also stitch `token` / `alt` when they were split off.
 */
function normalizeFirebaseThumbnailFromParams(searchParams, search) {
  let thumb = (searchParams.get("thumbnail") || "").trim();
  const tokenAlone = searchParams.get("token");

  if (tokenAlone && thumb && !/token=/.test(thumb)) {
    if (/alt=media/.test(thumb)) {
      thumb = `${thumb}&token=${encodeURIComponent(tokenAlone)}`;
    } else {
      const joiner = thumb.includes("?") ? "&" : "?";
      thumb = `${thumb}${joiner}alt=media&token=${encodeURIComponent(tokenAlone)}`;
    }
  }

  const raw = search ? extractThumbnailFromQueryString(search) : "";
  if (
    raw &&
    raw.startsWith("http") &&
    (raw.length > thumb.length ||
      (raw.includes("token=") && !thumb.includes("token=")))
  ) {
    thumb = raw;
  } else if ((!thumb || !thumb.startsWith("http")) && raw) {
    thumb = raw;
  }

  if (/%[0-9A-Fa-f]{2}/.test(thumb)) {
    try {
      const once = decodeURIComponent(thumb.replace(/\+/g, " "));
      if (once.startsWith("http")) thumb = once;
    } catch {
      /* keep */
    }
  }

  thumb = thumb.replace(
    /^https:\/\/firebasestorage\.googleapis\.com:443\//i,
    "https://firebasestorage.googleapis.com/"
  );

  return thumb;
}

function extractThumbnailFromQueryString(search) {
  const q = search.startsWith("?") ? search.slice(1) : search;
  const needle = "thumbnail=";
  const i = q.indexOf(needle);
  if (i === -1) return "";
  let raw = q.slice(i + needle.length);
  const nextKnown = raw.search(
    /&(galleryCode|galleryID|title|thumbnail)=/
  );
  if (nextKnown !== -1) raw = raw.slice(0, nextKnown);
  try {
    return decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    return raw;
  }
}

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function GalleryInviteView() {
  const [searchParams] = useSearchParams();
  const { search: locationSearch } = useLocation();
  const [fontsReady, setFontsReady] = useState(false);
  const [appOpening, setAppOpening] = useState(false);
  const [posts, setPosts] = useState(null);

  const galleryId = searchParams.get("galleryID") || "";
  const galleryCode = searchParams.get("galleryCode") || "";
  const titleParam = searchParams.get("title") || "";

  const displayTitle = useMemo(() => {
    if (!titleParam) return "Gallery invite";
    try {
      return decodeURIComponent(titleParam.replace(/\+/g, " "));
    } catch {
      return titleParam;
    }
  }, [titleParam]);

  const displayThumbnail = useMemo(() => {
    const normalized = normalizeFirebaseThumbnailFromParams(
      searchParams,
      locationSearch
    );
    if (!normalized) return DEFAULT_THUMB;
    return normalized;
  }, [searchParams, locationSearch]);

  const thumbnailForAppLink = useMemo(() => {
    const t = normalizeFirebaseThumbnailFromParams(
      searchParams,
      locationSearch
    );
    return t && t !== DEFAULT_THUMB ? t : "";
  }, [searchParams, locationSearch]);

  const universalLink = useMemo(() => {
    if (!galleryId) return "";
    let link = `https://app.moodstream.photos/galleryInvite?galleryID=${encodeURIComponent(galleryId)}`;
    if (galleryCode) link += `&galleryCode=${encodeURIComponent(galleryCode)}`;
    if (thumbnailForAppLink) {
      link += `&thumbnail=${encodeURIComponent(thumbnailForAppLink)}`;
    }
    if (titleParam) link += `&title=${encodeURIComponent(titleParam)}`;
    return link;
  }, [galleryId, galleryCode, thumbnailForAppLink, titleParam]);

  useEffect(() => {
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  useEffect(() => {
    if (!galleryId) return;
    get(ref(database, "posts/" + galleryId)).then((snapshot) => {
      if (snapshot.exists()) {
        const sorted = Object.values(snapshot.val()).sort(
          (a, b) => (b.timestamp || 0) - (a.timestamp || 0)
        );
        setPosts(sorted);
      } else {
        setPosts([]);
      }
    }).catch(() => {
      setPosts([]);
    });
  }, [galleryId]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = {
      htmlBg: html.style.background,
      htmlBgColor: html.style.backgroundColor,
      htmlMinH: html.style.minHeight,
      bodyBg: body.style.background,
      bodyBgColor: body.style.backgroundColor,
      bodyMinH: body.style.minHeight,
      rootBg: root?.style.background ?? "",
    };
    html.style.background = PAGE_BG;
    html.style.backgroundColor = BG_BOTTOM;
    html.style.minHeight = "100%";
    body.style.background = PAGE_BG;
    body.style.backgroundColor = BG_BOTTOM;
    body.style.minHeight = "100%";
    if (root) {
      root.style.minHeight = "100%";
      root.style.background = "transparent";
    }
    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundColor = prev.htmlBgColor;
      html.style.minHeight = prev.htmlMinH;
      body.style.background = prev.bodyBg;
      body.style.backgroundColor = prev.bodyBgColor;
      body.style.minHeight = prev.bodyMinH;
      if (root) root.style.background = prev.rootBg;
    };
  }, []);

  useEffect(() => {
    if (!galleryId) return;

    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const imageUrl = displayThumbnail || DEFAULT_THUMB;

    document.title = displayTitle;

    const metaByProperty = [
      { property: "og:type", content: "website" },
      { property: "og:title", content: displayTitle },
      { property: "og:description", content: SHARE_DESCRIPTION },
      { property: "og:image", content: imageUrl },
      { property: "og:image:secure_url", content: imageUrl },
      { property: "og:url", content: pageUrl },
    ];

    const metaByName = [
      { name: "description", content: SHARE_DESCRIPTION },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: displayTitle },
      { name: "twitter:description", content: SHARE_DESCRIPTION },
      { name: "twitter:image", content: imageUrl },
    ];

    metaByProperty.forEach(({ property, content }) =>
      setMetaProperty(property, content)
    );
    metaByName.forEach(({ name, content }) => setMetaName(name, content));
    setCanonical(pageUrl);

    const theme = document.querySelector('meta[name="theme-color"]');
    if (!theme) {
      const m = document.createElement("meta");
      m.setAttribute("name", "theme-color");
      m.setAttribute("content", BG_BOTTOM);
      document.head.appendChild(m);
    } else {
      theme.setAttribute("content", BG_BOTTOM);
    }

    return () => {
      document.title = "MoodStream";
    };
  }, [galleryId, displayTitle, displayThumbnail]);

  const handleOpenApp = (e) => {
    e?.preventDefault();
    if (!universalLink) return;
    setAppOpening(true);
    window.location.href = universalLink;
    setTimeout(() => setAppOpening(false), 2000);
  };

  if (!galleryId) {
    return (
      <>
        <style>{`
          .gi-missing {
            min-height: 100vh;
            min-height: 100dvh;
            background: ${PAGE_BG};
            background-color: ${BG_BOTTOM};
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: rgba(255,255,255,0.85);
            text-align: center;
          }
        `}</style>
        <div className="gi-missing">
          <p style={{ fontSize: 16 }}>This gallery invite link is missing a gallery ID.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @font-face {
          font-family: 'SF Pro Rounded Bold';
          src: url(${sfRoundedBold}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }
        @font-face {
          font-family: 'SF Pro Rounded Medium';
          src: url(${sfRoundedMedium}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }
        @font-face {
          font-family: 'SF Pro Rounded Light';
          src: url(${sfRoundedLight}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }

        @keyframes gi-spin { to { transform: rotate(360deg); } }

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .gi-page {
          min-height: 100vh;
          min-height: 100dvh;
          background: ${PAGE_BG};
          background-color: ${BG_BOTTOM};
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #fff;
          font-family: 'SF Pro Rounded Medium', -apple-system, BlinkMacSystemFont, sans-serif;
          padding-bottom: 140px;
        }

        /* ── Sticky topbar ── */
        .gi-topbar {
          position: sticky;
          top: 0;
          z-index: 100;
          align-self: stretch;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px 12px;
          background: rgba(8, 0, 15, 0.8);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(247, 0, 255, 0.12);
        }
        .gi-topbar-icon {
          width: 34px;
          height: 34px;
          border-radius: 9px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .gi-topbar-title {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 16px;
          font-weight: normal;
          color: rgba(255,255,255,0.75);
        }

        .gi-inner {
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          padding: 28px 12px 0;
        }

        .gi-title {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 30px;
          font-weight: normal;
          color: #fff;
          line-height: 1.2;
          letter-spacing: -0.02em;
          word-break: break-word;
          overflow-wrap: break-word;
          margin-bottom: 20px;
          text-align: center;
        }

        .gi-posts {
          display: flex;
          flex-direction: column;
          gap: 30px;
          align-items: center;
        }
        .gi-post {
          width: 85%;
          border-radius: 70px;
          overflow: hidden;
          background: #160025;
        }
        .gi-post img {
          width: 100%;
          display: block;
          object-fit: cover;
          image-orientation: from-image;
          opacity: 0;
          transition: opacity 0.18s ease-out;
        }
        .gi-post img.loaded { opacity: 1; }

        .gi-posts-loading {
          display: flex;
          justify-content: center;
          padding: 48px 0;
        }

        .gi-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(8, 0, 15, 0.88);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-top: 1px solid rgba(247, 0, 255, 0.15);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;
          padding: 12px 24px 22px;
        }
        .gi-footer-label {
          font-family: 'SF Pro Rounded Medium', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 17px;
          color: rgba(255,255,255,0.7);
          line-height: 1.35;
          text-align: center;
          white-space: normal;
          max-width: 360px;
        }
        .gi-footer-label strong {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #fff;
          font-weight: normal;
        }
        .gi-footer-inner {
          width: 100%;
          max-width: 360px;
        }
        .pv-cta {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 18px 28px;
          border: none;
          border-radius: 999px;
          background: ${ACCENT};
          color: #fff;
          font-size: 17px;
          font-weight: normal;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.15s ease;
        }
        .pv-cta:hover:not(:disabled) {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .pv-cta:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }
        .pv-cta-arrow {
          font-size: 1.15em;
          line-height: 1;
        }
      `}</style>

      <div className="gi-page">
        {!fontsReady ? (
          <div
            style={{
              minHeight: "100dvh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                border: `3px solid rgba(247, 0, 255, 0.2)`,
                borderTop: `3px solid ${ACCENT}`,
                borderRadius: "50%",
                animation: "gi-spin 0.9s linear infinite",
              }}
            />
          </div>
        ) : (
          <>
            <div className="gi-inner">
              <h1 className="gi-title">{displayTitle}</h1>

              <div className="gi-posts">
                {posts === null ? (
                  <div className="gi-posts-loading">
                    <div style={{
                      width: 32, height: 32,
                      border: `3px solid rgba(247, 0, 255, 0.2)`,
                      borderTop: `3px solid ${ACCENT}`,
                      borderRadius: "50%",
                      animation: "gi-spin 0.9s linear infinite",
                    }} />
                  </div>
                ) : posts.length > 0 ? (
                  posts.map((post, index) =>
                    post?.imgURL ? (
                      <div className="gi-post" key={index}>
                        <img
                          src={post.imgURL}
                          alt={`Post ${index + 1}`}
                          onLoad={(e) => e.target.classList.add("loaded")}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </div>
                    ) : null
                  )
                ) : (
                  <div className="gi-post">
                    <img
                      src={displayThumbnail}
                      alt=""
                      referrerPolicy="no-referrer"
                      onLoad={(e) => e.target.classList.add("loaded")}
                      onError={(e) => { e.target.src = DEFAULT_THUMB; }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="gi-footer">
              <p className="gi-footer-label">
                Join <strong>{displayTitle}</strong> on MoodStream
              </p>
              <div className="gi-footer-inner">
                <button
                  type="button"
                  className="pv-cta"
                  onClick={handleOpenApp}
                  disabled={appOpening || !universalLink}
                >
                  {appOpening ? "Opening…" : "Open MoodStream"}
                  <span className="pv-cta-arrow" aria-hidden>
                    →
                  </span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
